import { useRef, useCallback, Fragment, useMemo } from 'react';
import { Box, Divider,  } from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  PostType,
  CommentType,
  FlattenedFeedType,
  PaginatedPosts
} from '../memeverse/memeverse_types';
import { getUsersMyPosts } from '../../utils/apis/memeverse';
import { useAuth } from '../../hooks/useAuth';
import './../memeverse/feed/MemeReply.css';
import EmptyState from './EmptyState';
import MemeInProfilePage from '../memeverse/feed/MemeInProfilePage';
import { useAskWebsockets } from '../memeverse/hooks/useAskWebsockets';
import { useProfileData } from '../../hooks/useProfileData';
import { flattenCommentsList } from '../memeverse/helper';
import LoadingPage from '../LoadingPage';
import { RefreshStatesProvider, useRefreshActions } from '../memeverse/feed/RefreshStatesContext';

const MyPosts = ({ userId, onReplyClick }) => {
  return (
    <RefreshStatesProvider>
      <MainMyPosts userId={userId} onReplyClick={onReplyClick}/>
    </RefreshStatesProvider>
  );
}

const MainMyPosts = ({ userId, onReplyClick }) => {
  const { authUser } = useAuth();
  const { creatorProfileData } = useProfileData();
  const { triggerRefresh } = useRefreshActions();

  const { data, fetchNextPage, hasNextPage, isFetching, isLoading } =
    useInfiniteQuery({
      queryKey: ['userPosts', userId],
      queryFn: async ({ pageParam = 1 }) => {
        try {
          const resp = await getUsersMyPosts(
            authUser?.uid,
            userId,
            10,
            pageParam
          );
          if (resp.ok) {
            const data: PaginatedPosts = await resp.json();

            let posts = {} as Record<string, PostType>;
            let comments = {} as Record<string, CommentType>;
            let orderedPosts = [] as string[];

            for (let post of data.posts) {
              post.page_no = pageParam;
              posts[post.docId] = post;
              orderedPosts.push(post.docId);

              if (post.comments_list) {
                post.flat_comments_list = flattenCommentsList(
                  comments,
                  post.comments_list,
                  pageParam
                );
              } else {
                post.flat_comments_list = [];
              }
            }

            return {
              pagination: data.pagination,
              posts,
              comments,
              orderedPosts,
            } as FlattenedFeedType;
          }
          return {
            pagination: {},
            posts: {},
            comments: {},
            orderedPosts: [],
          } as FlattenedFeedType;
        } catch (err) {
          console.error('Error fetching posts:', err);
          return {
            pagination: {},
            posts: {},
            comments: {},
            orderedPosts: [],
          } as FlattenedFeedType;
        }
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage) =>
        lastPage.pagination?.has_next
          ? lastPage.pagination.next_page
          : undefined,
    });

  const observer = useRef<IntersectionObserver | null>(null);
  const loadMoreTriggerRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoading) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetching) {
          fetchNextPage();
        }
      });

      if (node) observer.current.observe(node);
    },
    [isLoading, hasNextPage, isFetching, fetchNextPage]
  );

  // Compile Infinite Query Data into flattened structure like feed
  const { userPosts, userComments, userOrderedPosts } = useMemo(() => {
    const userPosts: Record<string, PostType> = {};
    const userComments: Record<string, CommentType> = {};
    let userOrderedPosts: string[] = [];

    if (data?.pages) {
      data.pages.forEach((page) => {
        Object.assign(userPosts, page.posts);
        Object.assign(userComments, page.comments);
        userOrderedPosts = [...userOrderedPosts, ...page.orderedPosts];
      });
    }

    // Dedupe just in case
    userOrderedPosts = Array.from(new Set(userOrderedPosts));

    return { userPosts, userComments, userOrderedPosts };
  }, [data]);

  // Use websockets to listen for real-time updates to posts and comments
  const handleDocumentUpdate = useCallback((documentId: string, type: 'post' | 'comment') => {
    // Check if this document exists in the current data before triggering refresh
    if (type === 'post' && userPosts && userPosts[documentId]) {
      triggerRefresh(documentId);
    } else if (type === 'comment' && userComments && userComments[documentId]) {
      triggerRefresh(documentId);
    }
  }, [userPosts, userComments, triggerRefresh]);

  useAskWebsockets(['userPosts', userId], undefined, handleDocumentUpdate);

  return (
    <>
      {userOrderedPosts.length === 0 ? (
        isLoading ? (
          <Box
            display='flex'
            flexDirection='column'
            flex='1'
            flexGrow='1'
            width='100%'
            justifyContent='center'
            alignItems='center'
          >
            <LoadingPage />
          </Box>
        ) : (
          <EmptyState
            primaryMessage='No posts, yet.'
            secondaryMessage={`You will see a record of
              ${(authUser?.uid && creatorProfileData?.userId === authUser?.uid) ?
                'your' : `${creatorProfileData.fullName.split(' ')[0]}'s`} posts here.`}
            buttonTitle={
              (authUser?.uid && creatorProfileData.userId === authUser?.uid) ?
              'Create Post on Ask' : 'Go to Ask'}
            redirectUrl='/ask'
          />
        )
      ) : (
        <Box
          sx={{
            width: '100%',
            maxWidth: 'md',
            margin: 'auto',
            my: 4,
            gap: 2,
          }}
        >
          <>
            {userOrderedPosts.map((postId, index) => (
              <Fragment key={`${postId}-${index}`}>
                <MemeInProfilePage
                  post={userPosts[postId]}
                  allComments={userComments}
                  onReplyClick={onReplyClick}
                />
                <Divider sx={{ my: 1 }} />
              </Fragment>
            ))}

            {hasNextPage && (
              <Box
                ref={loadMoreTriggerRef}
                sx={{
                  height: '20px',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                {isFetching && <LoadingPage />}
              </Box>
            )}
          </>
        </Box>
      )}
    </>
  );
};

export default MyPosts;
